import { createContext, useContext, useEffect, useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

interface AuthContextType {
  token: string | undefined;
  customerId: string | undefined;
  loading: boolean;
  requestOtp: (phone: string) => void;
  verifyOtp: (phone: string, code: string) => Promise<boolean>;
  logout: () => void;
  isSignedIn: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState<string | undefined>(undefined);
  const [customerId, setCustomerId] = useState<string | undefined>(undefined);

  const { t } = useTranslation();
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token) {
      setToken(token);
    }
  }, []);

  const requestOtp = async (phone: string) => {
    setLoading(true);
    await axios.post(`https://kaman.rest/api/auth/send-otp?phone=${phone}`);
    setLoading(false);
  };

  const verifyOtp = async (phone: string, code: string): Promise<boolean> => {
    setLoading(true);

    try {
      const res = await axios.post(
        `https://kaman.rest/api/auth/verify-otp?phone=${phone}&code=${code}`
      );
      const token = res.data.data.token;
      const customerId = res.data.data.customer.id;
      localStorage.setItem("token", token);
      localStorage.setItem("customerId", customerId);
      setToken(token);
      setCustomerId(customerId);
      setLoading(false);
      toast.success(t("logged_in_successfully"));
      return true;
    } catch (error) {
      console.log(error);
      toast.error(t("invalid_otp"));
      return false;
    }
  };

  const logout = () => {
    setToken(undefined);
    setCustomerId(undefined);
    localStorage.removeItem("token");
    localStorage.removeItem("customerId");
    toast.success(t("logged_out_successfully"));
    navigate("/");
  };

  return (
    <AuthContext.Provider
      value={{
        token,
        customerId,
        loading,
        requestOtp,
        verifyOtp,
        logout,
        isSignedIn: !!token,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
